import * as signalR from '@microsoft/signalr'
import { CameraPerformance } from 'domain/camera-performance'
import { CameraEvent, EventType } from 'features/dashboard/types/camera-event'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { VehicleCounts } from 'domain/vehicle-counts'
import { useAuth } from './auth-context'
import { AlarmSignalR } from 'domain/alarm-signal-r'
import { CaptureStatusEvent } from 'domain/capture-status'
import { captureApiService } from 'features/dashboard/api/capture.api'
import { isCaptureStatusForClient } from 'features/dashboard/utils/capture-status'
import { tokenService } from 'http/services/token.service'

interface SignalRContextType {
  connection: signalR.HubConnection | null
  connected: boolean
  vehicleCount: VehicleCounts
  cameraPerformance: CameraPerformance[]
  eventHistory: CameraEvent[]
  totalEvents: number
  latestAlarm: AlarmSignalR | null
  alarmHistory: AlarmSignalR[]
  totalAlarms: number
  /** Alarmes recebidos desde a última vez que o feed foi aberto (ver markAlarmsAsRead). */
  unreadAlarmCount: number
  captureOnline: boolean
  clearEventHistory: () => void
  clearAlarmHistory: () => void
  markAlarmsAsRead: () => void
  setVehicleCount: React.Dispatch<React.SetStateAction<VehicleCounts>>
  setCameraPerformance: React.Dispatch<
    React.SetStateAction<CameraPerformance[]>
  >
}

const initialVehicleCounts: VehicleCounts = {
  cars: 0,
  bikes: 0,
  trucks: 0,
  people: 0,
  unknown: 0,
  buses: 0,
}

const SignalRContext = createContext<SignalRContextType>({
  connection: null,
  connected: false,
  vehicleCount: initialVehicleCounts,
  cameraPerformance: [],
  eventHistory: [],
  totalEvents: 0,
  latestAlarm: null,
  alarmHistory: [],
  totalAlarms: 0,
  unreadAlarmCount: 0,
  captureOnline: false,
  setVehicleCount: () => { },
  setCameraPerformance: () => { },
  clearEventHistory: () => { },
  clearAlarmHistory: () => { },
  markAlarmsAsRead: () => { },
})

export const useSignalR = () => useContext(SignalRContext)

interface SignalRProviderProps {
  children: ReactNode
}

const MAX_EVENT_HISTORY = 50
const MAX_ALARM_HISTORY = 50

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
}) => {
  const [connected, setConnected] = useState(false)
  const [eventHistory, setEventHistory] = useState<CameraEvent[]>([])
  const [totalEvents, setTotalEvents] = useState<number>(0)
  const [latestAlarm, setLatestAlarm] = useState<AlarmSignalR | null>(null)
  const [alarmHistory, setAlarmHistory] = useState<AlarmSignalR[]>([])
  const [totalAlarms, setTotalAlarms] = useState<number>(0)
  const [unreadAlarmCount, setUnreadAlarmCount] = useState<number>(0)
  const [captureOnline, setCaptureOnline] = useState<boolean>(false)
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  )
  const [vehicleCount, setVehicleCount] =
    useState<VehicleCounts>(initialVehicleCounts)
  const [cameraPerformance, setCameraPerformance] = useState<
    CameraPerformance[]
  >([])

  const { clientId } = useAuth()

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    const baseApiUrl: string = process.env.REACT_APP_SIGNALR_API_BASE_URL ?? ''

    // Don't initialize SignalR if user is not authenticated
    const isValidClientId =
      clientId && clientId.trim() !== '' && clientId !== 'client id not found'

    if (!isValidClientId) {
      console.info(
        '⏸️ SignalR: Waiting for authentication... (clientId:',
        clientId || 'empty',
        ')'
      )

      // Cleanup existing connection if user logged out
      if (connectionRef.current) {
        console.info('🔌 SignalR: Disconnecting due to logout...')
        connectionRef.current.stop().catch((err) => {
          console.error('Error stopping SignalR connection:', err)
        })
        connectionRef.current = null
        setConnection(null)
        setConnected(false)
        hasInitializedRef.current = false

        // Reset all state
        setEventHistory([])
        setTotalEvents(0)
        setLatestAlarm(null)
        setAlarmHistory([])
        setTotalAlarms(0)
        setUnreadAlarmCount(0)
        setVehicleCount(initialVehicleCounts)
        setCameraPerformance([])
        setCaptureOnline(false)
      }

      return
    }

    // Don't create a new connection if one already exists and is connected
    if (
      connectionRef.current &&
      (connectionRef.current.state === signalR.HubConnectionState.Connected ||
        connectionRef.current.state === signalR.HubConnectionState.Connecting)
    ) {
      console.info('✅ SignalR: Already connected or connecting')
      return
    }

    // Prevent duplicate initialization
    if (hasInitializedRef.current) {
      console.info('⏭️ SignalR: Already initialized, skipping')
      return
    }

    console.info('🔄 SignalR: Initializing connection...')
    hasInitializedRef.current = true

    initializeConnection(baseApiUrl)

    // Cleanup function
    return () => {
      console.info('🧹 SignalR: Cleanup triggered')

      if (connectionRef.current) {
        const currentState = connectionRef.current.state

        // Only stop if we're actually connected or connecting
        if (
          currentState === signalR.HubConnectionState.Connected ||
          currentState === signalR.HubConnectionState.Connecting
        ) {
          console.info(
            '🧹 SignalR: Stopping connection (state:',
            currentState,
            ')'
          )
          connectionRef.current.stop().catch((err) => {
            // Silently ignore AbortError during cleanup - it's expected in Strict Mode
            if (err.name !== 'AbortError') {
              console.error('Error stopping SignalR on cleanup:', err)
            }
          })
        }

        // ✅ CRITICAL: Reset refs on cleanup so second mount can initialize
        connectionRef.current = null
        hasInitializedRef.current = false
      }
    }
  }, [clientId])

  const initializeConnection = (baseApiUrl: string) => {
    const newConnection = new signalR.HubConnectionBuilder()
      // O token identifica de qual cliente é a conexão: o hub usa o claim
      // ClientId para colocá-la no grupo do próprio cliente, e só recebe
      // detecções/alarmes endereçados a esse grupo. Sem token, a conexão é
      // recusada ([Authorize] no CameraHub).
      .withUrl(baseApiUrl, {
        accessTokenFactory: () => tokenService.getAccessToken() ?? '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= 5) {
            return 30000
          }
          return Math.min(
            1000 * Math.pow(2, retryContext.previousRetryCount),
            30000
          )
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Store in ref to prevent multiple connections
    connectionRef.current = newConnection
    setConnection(newConnection)

    // Handle reconnection
    newConnection.onreconnecting((error) => {
      console.warn('🔄 SignalR: Reconnecting...', error)
      setConnected(false)
    })

    newConnection.onreconnected((connectionId) => {
      console.info('✅ SignalR: Reconnected with ID:', connectionId)
      setConnected(true)
    })

    newConnection.onclose((error) => {
      // Don't log AbortError - it's expected during Strict Mode cleanup
      if (error?.name === 'AbortError') {
        console.info('⏭️ SignalR: Connection aborted (Strict Mode)')
      } else if (error) {
        console.error('❌ SignalR: Connection closed', error)
        console.error('❌ Close reason:', error?.message || 'No error message')
      }
      setConnected(false)
    })

    newConnection.on('CameraEvent', (eventData: CameraEvent) => {
      // Route to appropriate handler based on event type
      if (
        eventData.eventType === EventType.ANPRDetection ||
        eventData.eventType === EventType.AHCRDetection
      ) {
        handleAnprAhcrEvent(eventData)
      } else if (
        eventData.eventType === EventType.CameraStarting ||
        eventData.eventType === EventType.CameraStopped ||
        eventData.eventType === EventType.CameraRestarting
      ) {
        handleCameraStatusEvent(eventData)
      }
    })

    newConnection.on('anpr-alarm', (alarmData: AlarmSignalR) => {
      setLatestAlarm(alarmData)

      setAlarmHistory((prev) => {
        const newHistory = [alarmData, ...prev]

        if (newHistory.length > MAX_ALARM_HISTORY) {
          return newHistory.slice(0, MAX_ALARM_HISTORY)
        }
        return newHistory
      })

      setTotalAlarms((prev) => prev + 1)
      setUnreadAlarmCount((prev) => prev + 1)
    })

    newConnection.on('capture-status', (payload: CaptureStatusEvent) => {
      // Hub broadcasts to all connections; only react to our own tenant's worker.
      if (isCaptureStatusForClient(payload, clientId)) {
        setCaptureOnline(payload.online)
      }
    })

    // Start the connection
    newConnection
      .start()
      .then(() => {
        console.info('✅ SignalR: Connected successfully 🚗🔥')
        console.log('✅ Connection ID:', newConnection.connectionId)
        console.log('✅ Connection state:', newConnection.state)
        setConnected(true)

        // Seed the capture indicator with the current status (live updates
        // afterwards arrive via the "capture-status" event above).
        if (clientId) {
          captureApiService
            .getCaptureStatus(clientId)
            .then((status) => setCaptureOnline(status.online))
            .catch(() => {
              // Non-fatal: indicator simply stays offline until first event/heartbeat.
            })
        }
      })
      .catch((err) => {
        // Silently ignore AbortError - it's expected in Strict Mode
        if (err.name !== 'AbortError') {
          console.error('❌ SignalR: Failed to connect:', err)
        }
        setConnected(false)
      })
  }

  const handleAnprAhcrEvent = (eventData: CameraEvent) => {
    const vehicleType = getVehicleTypeFromEvent(eventData)

    if (vehicleType) {
      setVehicleCount((prevCounts) => ({
        ...prevCounts,
        [vehicleType]: prevCounts[vehicleType] + 1,
      }))
    }

    setEventHistory((prev) => {
      const newHistory = [eventData, ...prev]

      if (newHistory.length > MAX_EVENT_HISTORY) {
        return newHistory.slice(0, MAX_EVENT_HISTORY)
      }
      return newHistory
    })

    setCameraPerformance((prev) => {
      if (prev.length <= 0) {
        return prev
      }

      const existingIndex = prev.findIndex(
        (camera) => camera.cameraName === eventData.cameraId
      )

      if (existingIndex >= 0) {
        return prev.map((camera, index) =>
          index === existingIndex
            ? { ...camera, eventCount: camera.eventCount + 1 }
            : camera
        )
      } else {
        return [
          ...prev,
          {
            cameraName: eventData.cameraId,
            eventCount: 1,
          },
        ]
      }
    })

    setTotalEvents((prev) => prev + 1)
  }

  const handleCameraStatusEvent = (eventData: CameraEvent) => {
    // Add camera status events to event history
    setEventHistory((prev) => {
      const newHistory = [eventData, ...prev]

      if (newHistory.length > MAX_EVENT_HISTORY) {
        return newHistory.slice(0, MAX_EVENT_HISTORY)
      }
      return newHistory
    })
  }

  const getVehicleTypeFromEvent = (
    event: CameraEvent
  ): keyof VehicleCounts | null => {
    const vehicleType = event.metadata?.VehicleType

    if (
      vehicleType &&
      ['cars', 'bikes', 'trucks', 'people', 'buses', 'unknown'].includes(
        vehicleType
      )
    ) {
      return vehicleType as keyof VehicleCounts
    }

    return null
  }

  const clearEventHistory = () => {
    setEventHistory([])
  }

  const clearAlarmHistory = () => {
    setAlarmHistory([])
    setLatestAlarm(null)
  }

  const markAlarmsAsRead = () => {
    setUnreadAlarmCount(0)
  }

  return (
    <SignalRContext.Provider
      value={{
        connection,
        connected,
        vehicleCount,
        cameraPerformance,
        eventHistory,
        totalEvents,
        latestAlarm,
        alarmHistory,
        totalAlarms,
        unreadAlarmCount,
        captureOnline,
        setVehicleCount,
        setCameraPerformance,
        clearEventHistory,
        clearAlarmHistory,
        markAlarmsAsRead,
      }}
    >
      {children}
    </SignalRContext.Provider>
  )
}
